import React, { useCallback, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import AssetModuleNavbar from "./AssetModuleNavbar";
import API_BASE_URL from "../config";

const STATUS_BADGE = {
  PENDING: "bg-warning text-dark",
  APPROVED: "bg-success",
  REJECTED: "bg-danger",
  ACTIVE: "bg-primary",
};

export default function PendingApprovalsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("pending");
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, request: null, remark: "" });
  const token = localStorage.getItem("token");
  const employeeId = localStorage.getItem("employeeId");
  const [navbarColor, setNavbarColor] = useState("#080235");

  useEffect(() => {
    const bootstrapLink = document.createElement("link");
    bootstrapLink.rel = "stylesheet";
    bootstrapLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
    document.head.appendChild(bootstrapLink);

    const iconsLink = document.createElement("link");
    iconsLink.rel = "stylesheet";
    iconsLink.href =
      "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
    document.head.appendChild(iconsLink);

    return () => {
      bootstrapLink.remove();
      iconsLink.remove();
    };
  }, []);

  useEffect(() => {
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) {
      toast.error("You must be logged in.");
      navigate("/");
      return;
    }
    // if (!roles.includes("ROLE_MANAGER") && !roles.includes("ROLE_ADMIN")) {
    //   toast.error("Unauthorized.");
    //   navigate("/");
    // }
  }, [navigate, token]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;
    fetch(`${API_BASE_URL}/api/appearance/${userId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); }).catch(() => { });
  }, [token]);

  const fetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const roles = JSON.parse(localStorage.getItem("roles") || "[]");
     const isAdmin = roles.includes("ROLE_ADMIN");
      const isUser = roles.includes("ROLE_USER");
      const isManager = roles.includes("ROLE_MANAGER");

      let url = "";

      if (isAdmin || isUser) {
        // User also gets admin API access
        url = `${API_BASE_URL}/api/asset-requests?page=0&size=100`;
      } else if (isManager) {
        url = `${API_BASE_URL}/api/asset-requests/manager/${employeeId}`;
      }

      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

      const data = res.ok ? await res.json() : {};
      const all = Array.isArray(data) ? data : data.content || [];

      setPendingApprovals(all.filter(r => r.status === "Pending" || r.status === "PENDING"));
      setHistory(all.filter(r => r.status !== "Pending" && r.status !== "PENDING"));
    } catch {
      setPendingApprovals([]);
      setHistory([]);
      toast.error("Unable to load approval queue.");
    } finally {
      setLoading(false);
    }
  }, [token, employeeId]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleAction = async (request, action, adminRemarks = "") => {
    try {
      setProcessingId(request.id);
      const body = { status: action === "approve" ? "Approved" : "Rejected" };
      if (adminRemarks.trim()) body.adminRemarks = adminRemarks.trim();
      const res = await fetch(`${API_BASE_URL}/api/asset-requests/${request.id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(await res.text());

      toast.success(action === "approve" ? "Request approved." : "Request rejected.");
      fetchApprovals();
    } catch (err) {
      toast.error(err.message || "Unable to process.");
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (request) => setRejectModal({ open: true, request, remark: "" });
  const closeRejectModal = () => setRejectModal({ open: false, request: null, remark: "" });
  const confirmReject = () => {
    handleAction(rejectModal.request, "reject", rejectModal.remark);
    closeRejectModal();
  };

  const pendingCount = pendingApprovals.length;
  const approvedCount = history.filter((item) => item.status === "APPROVED").length;
  const rejectedCount = history.filter((item) => item.status === "REJECTED").length;

  return (
    <>
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="fixed-top">
        <AssetModuleNavbar />
      </div>
      <div className="container-fluid my-4" style={{ paddingTop: "90px" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="m-0">
              <i className="bi bi-check2-square me-2"></i> Asset Approvals
            </h4>
            <div className="text-muted mt-1" style={{ fontSize: 13 }}>
              Review employee allocation requests and keep the handover trail complete.
            </div>
          </div>
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
        </div>

        <div className="row g-2 mb-3">
          {[
            { label: "Pending", value: pendingCount, color: "#fd7e14", icon: "bi-hourglass-split" },
            { label: "Approved", value: approvedCount, color: "#198754", icon: "bi-check-circle" },
            { label: "Rejected", value: rejectedCount, color: "#dc3545", icon: "bi-x-circle" },
          ].map((card) => (
            <div className="col-md-4" key={card.label}>
              <div className="bg-white border rounded p-3 d-flex align-items-center gap-3">
                <i className={`bi ${card.icon}`} style={{ fontSize: 24, color: card.color }}></i>
                <div>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                    {card.label}
                  </div>
                  <div className="fw-bold" style={{ fontSize: 22, color: card.color }}>
                    {card.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <ul className="nav nav-tabs mb-0" style={{ backgroundColor: navbarColor }}>
          {[
            ["pending", "Pending Queue"],
            ["history", "Approval History"],
          ].map(([key, label]) => (
            <li className="nav-item" key={key}>
              <button
                type="button"
                className={`nav-link ${activeTab === key ? "active" : ""}`}
                onClick={() => setActiveTab(key)}
                style={{
                  color: activeTab === key ? navbarColor : "#fff",
                  backgroundColor: activeTab === key ? "#fff" : "transparent",
                  fontWeight: activeTab === key ? 600 : "normal",
                }}
              >
                {label}
              </button>
            </li>
          ))}
        </ul>

        <div className="p-3 border border-top-0 bg-white">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary"></div>
            </div>
          ) : activeTab === "pending" ? (
            pendingApprovals.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bi bi-check2-all" style={{ fontSize: 28, opacity: 0.4 }}></i>
                <div className="mt-2">No pending approvals right now.</div>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table table-sm align-middle text-start mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Asset</th>
                      <th>Employee</th>
                      <th>Priority</th>
                      <th>Expected By</th>
                      <th>Justification</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingApprovals.map((item) => (
                      <tr key={item.id}>
                        <td style={{ fontWeight: 600 }}>{item.assetCategory || "—"}</td>
                        <td>{item.employeeName || item.employeeId || "—"}</td>
                        <td>{item.priority || "—"}</td>
                        <td>{item.expectedDate || "—"}</td>
                        <td style={{ maxWidth: 220 }}>
                          <small className="text-muted">{item.justification || "No justification"}</small>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <button className="btn btn-sm btn-outline-success"
                              onClick={() => handleAction(item, "approve")}
                              disabled={processingId === item.id}>Approve</button>
                            <button className="btn btn-sm btn-outline-danger"
                              onClick={() => openRejectModal(item)}
                              disabled={processingId === item.id}>Reject</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : history.length === 0 ? (
            <p className="text-muted mb-0">No approval history found.</p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="table table-sm align-middle text-start mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Asset</th>
                    <th>Employee</th>
                    <th>Requested On</th>
                    <th>Expected By</th>
                    <th>Justification</th>
                    <th>Status</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((item) => (
                    <tr key={item.id}>
                      <td style={{ fontWeight: 600 }}>{item.assetCategory || "—"}</td>
                      <td>{item.employeeName || item.employeeId || "—"}</td>
                      <td>{item.requestedDate || item.createdAt || "—"}</td>
                      <td>{item.expectedDate || "—"}</td>
                      <td style={{ maxWidth: 200 }}><small className="text-muted">{item.justification || "—"}</small></td>
                      <td>
                        <span className={`badge ${STATUS_BADGE[item.status] || "bg-secondary"}`}>
                          {item.status || "UNKNOWN"}
                        </span>
                      </td>
                      <td style={{ maxWidth: 200 }}><small className="text-muted">{item.adminRemarks || "—"}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Reject Remark Modal ── */}
      {rejectModal.open && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
          onClick={closeRejectModal}>
          <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", width: "100%", maxWidth: 460, padding: "28px 28px 22px" }}
            onClick={e => e.stopPropagation()}>
            <div className="d-flex align-items-center gap-3 mb-3">
              <div style={{ width: 42, height: 42, borderRadius: 10, background: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-x-circle-fill" style={{ fontSize: 20, color: "#dc3545" }} />
              </div>
              <div>
                <div className="fw-bold" style={{ fontSize: 16 }}>Reject Request</div>
                <div className="text-muted" style={{ fontSize: 13 }}>
                  {rejectModal.request?.assetCategory} — {rejectModal.request?.employeeName}
                </div>
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                Rejection Remark <span className="text-muted fw-normal">(optional)</span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Provide a reason for rejection…"
                value={rejectModal.remark}
                onChange={e => setRejectModal(p => ({ ...p, remark: e.target.value }))}
                autoFocus
              />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-outline-secondary btn-sm px-4" onClick={closeRejectModal}>Cancel</button>
              <button className="btn btn-danger btn-sm px-4" onClick={confirmReject}
                disabled={processingId === rejectModal.request?.id}>
                {processingId === rejectModal.request?.id
                  ? <><span className="spinner-border spinner-border-sm me-1" />Rejecting…</>
                  : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
